import { ControlModeTemplateFieldsType } from '@src/types/ControlModes';
import {
  DeleteItem,
  FormAction,
  FormActionType,
  FormState,
  FormValue,
} from './types';

// Re-export types for use in other files
export { FormActionType };
export type { FormState, FormValue, DeleteItem };

// Define initial state
export const initialState: FormState = {
  name: '',
  formValues: {},
  originalValues: {},
  unsavedSettings: null,
  groupedFields: [],

  showAddField: false,
  isEditMode: false,
  editingField: null,
  selectedSettingIndex: 0,
  newFieldData: {
    uiLabel: '',
    controllerField: '',
    type: ControlModeTemplateFieldsType.STRING,
    defaultValue: '',
    quickConfig: true,
    range: { min: undefined, max: undefined },
    listOptions: '',
  },
  newFieldErrors: {},
  parsedOptions: [],
  newOption: '',

  newSettingData: {
    uiLabel: '',
    controllerField: '',
  },
  newSettingErrors: {},
  showAddSetting: false,
  isSettingEditMode: false,
  editingSetting: null,
  pendingSetting: null,

  hasAttemptedSubmit: false,
  isSaving: false,
  fieldErrors: {},
  snackbars: [],
  snackbarOpen: false,
  snackbarMessage: '',
  snackbarSeverity: 'success',
  deleteConfirmOpen: false,
  deleteItemType: 'field',
  itemToDelete: null,
};

// Create the reducer function
export function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case FormActionType.SET_NAME:
      return { ...state, name: action.payload };

    case FormActionType.SET_FORM_VALUES:
      return { ...state, formValues: action.payload };

    case FormActionType.UPDATE_FORM_VALUE: {
      // Update the form value
      const updatedState = {
        ...state,
        formValues: {
          ...state.formValues,
          [action.payload.name]: action.payload.value,
        },
      };

      // Also update the defaultValue in unsavedSettings if it exists
      if (state.unsavedSettings) {
        const updatedSettings = [...state.unsavedSettings];

        // Find the setting and field to update
        for (const setting of updatedSettings) {
          const fieldIndex = setting.fields.findIndex(
            (field) => field.controllerField === action.payload.name,
          );

          if (fieldIndex !== -1) {
            // Update the defaultValue of the field
            const field = setting.fields[fieldIndex];
            const newDefaultValue =
              action.payload.defaultValue === null
                ? undefined
                : action.payload.defaultValue;

            // Ensure the defaultValue matches the field type
            let typedDefaultValue: string | number | boolean | undefined;
            if (
              field.type === ControlModeTemplateFieldsType.STRING ||
              field.type === ControlModeTemplateFieldsType.SELECT
            ) {
              typedDefaultValue =
                newDefaultValue !== undefined
                  ? String(newDefaultValue)
                  : undefined;
            } else if (field.type === ControlModeTemplateFieldsType.NUMBER) {
              typedDefaultValue =
                newDefaultValue !== undefined
                  ? Number(newDefaultValue)
                  : undefined;
            } else if (field.type === ControlModeTemplateFieldsType.BOOLEAN) {
              typedDefaultValue =
                newDefaultValue !== undefined
                  ? Boolean(newDefaultValue)
                  : undefined;
            } else {
              typedDefaultValue = undefined;
            }

            // Use type assertion to ensure TypeScript knows this is a valid ControlModeTemplateField
            if (
              field.type === ControlModeTemplateFieldsType.STRING ||
              field.type === ControlModeTemplateFieldsType.SELECT
            ) {
              setting.fields[fieldIndex] = {
                ...field,
                defaultValue: typedDefaultValue as string | undefined,
              };
            } else if (field.type === ControlModeTemplateFieldsType.NUMBER) {
              setting.fields[fieldIndex] = {
                ...field,
                defaultValue: typedDefaultValue as number | undefined,
              };
            } else if (field.type === ControlModeTemplateFieldsType.BOOLEAN) {
              setting.fields[fieldIndex] = {
                ...field,
                defaultValue: typedDefaultValue as boolean | undefined,
              };
            }

            // Update the unsavedSettings in the state
            updatedState.unsavedSettings = updatedSettings;

            // Update the value in formValues to match the new default value
            updatedState.formValues = {
              ...updatedState.formValues,
              [action.payload.name]: action.payload.defaultValue,
            };

            // Update the groupedFields to reflect the change
            updatedState.groupedFields = state.groupedFields.map((group) =>
              group.controllerField === setting.controllerField
                ? {
                    ...group,
                    fields: setting.fields,
                  }
                : group,
            );

            break;
          }
        }
      }

      return updatedState;
    }

    case FormActionType.SET_ORIGINAL_VALUES:
      return { ...state, originalValues: action.payload };

    case FormActionType.SET_UNSAVED_SETTINGS:
      return { ...state, unsavedSettings: action.payload };

    case FormActionType.SET_GROUPED_FIELDS:
      return { ...state, groupedFields: action.payload };

    case FormActionType.TOGGLE_ADD_FIELD:
      return {
        ...state,
        showAddField: !state.showAddField,
        newFieldErrors: {},
        // Reset editing state when toggling
        editingField: null,
        isEditMode: false,
      };

    case FormActionType.SET_SHOW_ADD_FIELD:
      return { ...state, showAddField: action.payload };

    case FormActionType.SET_IS_EDIT_MODE:
      return { ...state, isEditMode: action.payload };

    case FormActionType.SET_EDITING_FIELD:
      return { ...state, editingField: action.payload };

    case FormActionType.SET_SELECTED_SETTING_INDEX:
      return { ...state, selectedSettingIndex: action.payload };

    case FormActionType.SET_NEW_FIELD_DATA:
      return {
        ...state,
        newFieldData: { ...state.newFieldData, ...action.payload },
      };

    case FormActionType.RESET_NEW_FIELD_DATA:
      return {
        ...state,
        newFieldData: {
          uiLabel: '',
          controllerField: '',
          type: ControlModeTemplateFieldsType.STRING,
          defaultValue: '',
          quickConfig: true,
          range: { min: undefined, max: undefined },
          listOptions: '',
        },
        parsedOptions: [],
        newOption: '',
      };

    case FormActionType.SET_NEW_FIELD_ERRORS:
      return { ...state, newFieldErrors: action.payload };

    case FormActionType.SET_PARSED_OPTIONS:
      return { ...state, parsedOptions: action.payload };

    case FormActionType.SET_NEW_OPTION:
      return { ...state, newOption: action.payload };

    case FormActionType.ADD_OPTION: {
      if (!state.newOption.trim()) {
        return state;
      }

      const newOptions = [...state.parsedOptions];
      if (!newOptions.includes(state.newOption.trim())) {
        newOptions.push(state.newOption.trim());
      }

      // Set the first option as default if defaultValue is empty
      const defaultValue =
        state.newFieldData.defaultValue === '' && newOptions.length > 0
          ? newOptions[0]
          : state.newFieldData.defaultValue;

      // Clear the "At least one option is required" error if we now have options
      const updatedErrors = { ...state.newFieldErrors };
      if (
        newOptions.length > 0 &&
        updatedErrors['listOptions'] === 'At least one option is required'
      ) {
        delete updatedErrors['listOptions'];
      }

      return {
        ...state,
        parsedOptions: newOptions,
        newOption: '',
        newFieldData: {
          ...state.newFieldData,
          listOptions: newOptions.join(','),
          defaultValue,
        },
        newFieldErrors: updatedErrors,
      };
    }

    case FormActionType.REMOVE_OPTION: {
      const optionToRemove = action.payload;
      const filteredOptions = state.parsedOptions.filter(
        (option) => option !== optionToRemove,
      );

      // Update defaultValue if the removed option was the default
      let updatedDefaultValue = state.newFieldData.defaultValue;
      if (state.newFieldData.defaultValue === optionToRemove) {
        // Set to first available option or empty string if no options left
        updatedDefaultValue =
          filteredOptions.length > 0 ? filteredOptions[0] : '';
      }

      // Add the "At least one option is required" error if we have no options left
      const updatedErrors = { ...state.newFieldErrors };
      if (
        filteredOptions.length === 0 &&
        state.newFieldData.type === ControlModeTemplateFieldsType.SELECT
      ) {
        updatedErrors['listOptions'] = 'At least one option is required';
      }

      return {
        ...state,
        parsedOptions: filteredOptions,
        newFieldData: {
          ...state.newFieldData,
          listOptions: filteredOptions.join(','),
          defaultValue: updatedDefaultValue,
        },
        newFieldErrors: updatedErrors,
      };
    }

    case FormActionType.SET_NEW_SETTING_DATA:
      return {
        ...state,
        newSettingData: { ...state.newSettingData, ...action.payload },
      };

    case FormActionType.RESET_NEW_SETTING_DATA:
      return {
        ...state,
        newSettingData: {
          uiLabel: '',
          controllerField: '',
        },
      };

    case FormActionType.SET_NEW_SETTING_ERRORS:
      return { ...state, newSettingErrors: action.payload };

    case FormActionType.TOGGLE_ADD_SETTING:
      return {
        ...state,
        showAddSetting: !state.showAddSetting,
        newSettingErrors: {},
        // If we're showing add setting, hide add field
        showAddField: state.showAddSetting ? state.showAddField : false,
        // Reset editing state
        editingField: null,
      };

    case FormActionType.SET_SHOW_ADD_SETTING:
      return { ...state, showAddSetting: action.payload };

    case FormActionType.SET_IS_SETTING_EDIT_MODE:
      return { ...state, isSettingEditMode: action.payload };

    case FormActionType.SET_EDITING_SETTING:
      return { ...state, editingSetting: action.payload };

    case FormActionType.SET_PENDING_SETTING:
      return { ...state, pendingSetting: action.payload };

    case FormActionType.SET_HAS_ATTEMPTED_SUBMIT:
      return { ...state, hasAttemptedSubmit: action.payload };

    case FormActionType.SET_IS_SAVING:
      return { ...state, isSaving: action.payload };

    case FormActionType.SET_FIELD_ERRORS:
      return { ...state, fieldErrors: action.payload };

    case FormActionType.SHOW_SNACKBAR: {
      // Generate a unique ID for the new snackbar
      const newSnackbarId = `snackbar-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      // Add the new snackbar to the array
      const newSnackbar = {
        id: newSnackbarId,
        message: action.payload.message,
        severity: action.payload.severity,
      };

      // For backward compatibility, also update the single snackbar properties
      return {
        ...state,
        snackbars: [...state.snackbars, newSnackbar],
        snackbarOpen: true,
        snackbarMessage: action.payload.message,
        snackbarSeverity: action.payload.severity,
      };
    }

    case FormActionType.HIDE_SNACKBAR:
      // If a specific snackbar ID is provided, remove only that snackbar
      if (action.payload?.id) {
        return {
          ...state,
          snackbars: state.snackbars.filter(
            (snackbar) => snackbar.id !== action.payload?.id,
          ),
          // For backward compatibility, if we're removing the last snackbar, also update the single snackbar property
          ...(state.snackbars.length === 1 &&
          state.snackbars[0].id === action.payload?.id
            ? { snackbarOpen: false }
            : {}),
        };
      }

      // If no ID is provided, for backward compatibility, just set snackbarOpen to false
      return {
        ...state,
        snackbarOpen: false,
      };

    case FormActionType.SHOW_DELETE_CONFIRM:
      return {
        ...state,
        deleteConfirmOpen: true,
        deleteItemType: action.payload.itemType,
        itemToDelete: action.payload.item,
      };

    case FormActionType.HIDE_DELETE_CONFIRM:
      return {
        ...state,
        deleteConfirmOpen: false,
        itemToDelete: null,
      };

    case FormActionType.ADD_NEW_FIELD:
      // This is a complex operation that will be handled in the context provider
      return state;

    case FormActionType.ADD_NEW_SETTING:
      // This is a complex operation that will be handled in the context provider
      return state;

    case FormActionType.DELETE_FIELD:
      // This is a complex operation that will be handled in the context provider
      return state;

    case FormActionType.DELETE_SETTING:
      // This is a complex operation that will be handled in the context provider
      return state;

    case FormActionType.TOGGLE_QUICK_CONFIG:
      // This is a complex operation that will be handled in the context provider
      return state;

    case FormActionType.RESET_FORM:
      return {
        ...initialState,
        // Keep UI state like snackbars
        snackbars: state.snackbars,
        snackbarOpen: state.snackbarOpen,
        snackbarMessage: state.snackbarMessage,
        snackbarSeverity: state.snackbarSeverity,
      };

    default:
      return state;
  }
}
